import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getPricingPlans, deletePricingPlan, addPlanFeature, removePlanFeature } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { Spinner } from '../../components/ui/Spinner'
import { PageHeader } from '../../components/ui/PageHeader'
import { EmptyState } from '../../components/ui/EmptyState'
import { ConfirmModal, useConfirm } from '../../components/ui/ConfirmModal'

export default function PricingList() {
  const [expanded, setExpanded] = useState(null)
  const [featureText, setFeatureText] = useState('')
  const qc = useQueryClient()
  const { confirm, close, state: confirmState } = useConfirm()

  const { data, isLoading } = useQuery({ queryKey: ['pricing'], queryFn: getPricingPlans })
  const deleteMut = useMutation({
    mutationFn: deletePricingPlan,
    onSuccess: () => { qc.invalidateQueries(['pricing']); toast.success('Plan deleted') },
    onError: (e) => toast.error(e.message),
  })
  const addFeature = useMutation({
    mutationFn: ({ id, text }) => addPlanFeature(id, { text, order: 0 }),
    onSuccess: () => { qc.invalidateQueries(['pricing']); setFeatureText('') },
    onError: (e) => toast.error(e.message),
  })
  const removeFeature = useMutation({
    mutationFn: ({ id, fid }) => removePlanFeature(id, fid),
    onSuccess: () => qc.invalidateQueries(['pricing']),
    onError: (e) => toast.error(e.message),
  })

  return (
    <div>
      <PageHeader
        title="Pricing Plans"
        description="Manage subscription and pricing tiers"
        action={<Link to="/pricing/new"><Button><i className="ti ti-plus" /> New Plan</Button></Link>}
      />

      <ConfirmModal open={confirmState.open} onClose={close} title={confirmState.title} message={confirmState.message} loading={deleteMut.isPending} onConfirm={() => { confirmState.onConfirm(); close() }} />

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
      ) : !data?.length ? (
        <EmptyState icon="ti-currency-dollar" title="No pricing plans" description="Create your first pricing plan"
          action={<Link to="/pricing/new"><Button size="sm">Add Plan</Button></Link>} />
      ) : (
        <div className="space-y-3">
          {data.map(plan => (
            <div key={plan.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center gap-4 px-5 py-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${plan.isHighlighted ? 'bg-indigo-600' : 'bg-gray-100'}`}>
                  <i className={`ti ti-crown text-lg ${plan.isHighlighted ? 'text-white' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900">{plan.name}</p>
                    <span className="font-bold text-indigo-600">{plan.price}</span>
                    {plan.period && <span className="text-sm text-gray-400">/ {plan.period}</span>}
                    {plan.badge && <Badge color="purple">{plan.badge}</Badge>}
                    {plan.isHighlighted && <Badge color="indigo">Featured</Badge>}
                    <Badge color={plan.isActive ? 'green' : 'gray'}>{plan.isActive ? 'Active' : 'Inactive'}</Badge>
                  </div>
                  <p className="text-sm text-gray-400 mt-0.5 truncate">{plan.description}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="ghost" size="xs" className="text-gray-400" onClick={() => setExpanded(expanded === plan.id ? null : plan.id)}>
                    <i className={`ti ti-chevron-${expanded === plan.id ? 'up' : 'down'}`} />
                  </Button>
                  <Link to={`/pricing/${plan.id}/edit`}>
                    <Button variant="ghost" size="xs" className="text-gray-400 hover:text-indigo-600"><i className="ti ti-pencil" /></Button>
                  </Link>
                  <Button variant="ghost" size="xs" className="text-gray-400 hover:text-red-600"
                    onClick={() => confirm({ title: 'Delete Plan', message: `Delete "${plan.name}"? This action cannot be undone.`, onConfirm: () => deleteMut.mutate(plan.id) })}>
                    <i className="ti ti-trash" />
                  </Button>
                </div>
              </div>

              {expanded === plan.id && (
                <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/30">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Plan Features</p>
                  {plan.features?.length ? (
                    <div className="space-y-1.5 mb-3">
                      {plan.features.map(f => (
                        <div key={f.id} className="flex items-center gap-2 text-sm">
                          <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <i className="ti ti-check text-emerald-600 text-xs" />
                          </div>
                          <span className="flex-1 text-gray-700">{f.text}</span>
                          <button onClick={() => removeFeature.mutate({ id: plan.id, fid: f.id })} className="text-gray-300 hover:text-red-500">
                            <i className="ti ti-x text-sm" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-sm text-gray-400 mb-3">No features added yet.</p>}
                  <div className="flex gap-2">
                    <input
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
                      placeholder="Add a feature..."
                      value={featureText}
                      onChange={e => setFeatureText(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && featureText.trim() && addFeature.mutate({ id: plan.id, text: featureText.trim() })}
                    />
                    <Button size="sm" onClick={() => featureText.trim() && addFeature.mutate({ id: plan.id, text: featureText.trim() })}>Add</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
